/**
 * useLocalStorage — Generic hook for reading/writing values to localStorage with type safety.
 */
import { useState, useEffect, useRef } from 'react';
import { logger } from '@/utils/logger';

export function useLocalStorage<T>(key: string, initialValue: T) {
  const prefix = 'goal_tracker_';
  const prefixedKey = `${prefix}${key}`;

  const [storedValue, setStoredValue] = useState<T>(() => {
    if (typeof window === 'undefined') {
      return initialValue;
    }
    try {
      const item = window.localStorage.getItem(prefixedKey);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      logger.error('Error reading from localStorage:', error);
      return initialValue;
    }
  });

  const setValue = (value: T | ((val: T) => T)) => {
    try {
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      setStoredValue(valueToStore);
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(prefixedKey, JSON.stringify(valueToStore));
      }
    } catch (error) {
      logger.error('Error saving to localStorage:', error);
    }
  };

  const getItem = (itemKey?: string) => {
    const keyToUse = itemKey ? `${prefix}${itemKey}` : prefixedKey;
    try {
      const item = window.localStorage.getItem(keyToUse);
      return item ? JSON.parse(item) : null;
    } catch (error) {
      logger.error('Error getting from localStorage:', error);
      return null;
    }
  };

  const setItem = (itemKey: string, value: any) => {
    const keyToUse = `${prefix}${itemKey}`;
    try {
      window.localStorage.setItem(keyToUse, JSON.stringify(value));
    } catch (error) {
      logger.error('Error setting in localStorage:', error);
    }
  };

  // When the storage key changes (e.g. user signs in/out and the key becomes
  // user-scoped), reset the in-memory value so one account's data can never
  // leak into another session (or into guest mode).
  const prevKeyRef = useRef(prefixedKey);
  useEffect(() => {
    if (prevKeyRef.current !== prefixedKey) {
      prevKeyRef.current = prefixedKey;
      try {
        const item = window.localStorage.getItem(prefixedKey);
        setStoredValue(item ? JSON.parse(item) : initialValue);
      } catch {
        setStoredValue(initialValue);
      }
    }
  }, [prefixedKey, initialValue]);

  useEffect(() => {
    if (prevKeyRef.current !== prefixedKey) return;
    try {
      const item = window.localStorage.getItem(prefixedKey);
      const parsedItem = item ? JSON.parse(item) : null;
      
      if (parsedItem !== null && JSON.stringify(parsedItem) !== JSON.stringify(storedValue)) {
        setStoredValue(parsedItem);
      } else if (parsedItem === null) {
        window.localStorage.setItem(prefixedKey, JSON.stringify(initialValue));
      }
    } catch (error) {
      logger.error('Error syncing with localStorage:', error);
    }
  }, [prefixedKey, initialValue, storedValue]);

  return {
    value: storedValue,
    setValue,
    getItem,
    setItem
  };
}
